import { useEffect, useRef, useState, type FocusEvent, type KeyboardEvent, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { SECTIONS, currentMarker, sectionHref, sectionBadge, accountMarker, railIsOpen, lockLabel, lockFromStored } from "../lib/nav";
import { readPref, writePref } from "../lib/viewPrefs";
import { href, type Route } from "../lib/route";
import { useShellCounts } from "../routes/useShellCounts";
import { BrandLockup } from "./BrandLockup";
import { CoachCard } from "./CoachCard";
import { Icon } from "./Icon";
import { ThemeControl } from "./ThemeControl";
import { SearchControl } from "./SearchControl";

/* The navigation shell — three columns; the first is an icon rail that opens to the labelled sidebar, or that sidebar
 * locked open (W140).
 *
 * `docs/WEB-APP-PLAN.md` §9. The owner's words were *"everfit has everything on the left side,
 * i mean all pages, and then the subpages. it's more tidy"*, and §9.1 records what the
 * measurement says he meant: the thing worth taking is the SELECTION MODEL — column 2 is the
 * list you are choosing from and column 3 is always the detail of that selection — not the page
 * tree, which §29.3 found in one of their six sections and nowhere else.
 *
 * ⚠️ IT DOES NOT FIGHT §2b, WHICH IS WORTH SAYING BECAUSE IT LOOKS LIKE IT MIGHT. *Minimal
 * surface* is about how many controls a screen puts in front of you at once. A shell that makes
 * the whole product visible in two columns is what delivers the third part of his rule —
 * *"feel like you almost own it, even before you sit and watch some tutorial"*. A product with
 * no visible structure is not minimal, it is unlearnable.
 *
 * ── WHY THIS IS TWO COMPONENTS AND NOT ONE ────────────────────────────────────────────────
 *
 * `Shell` owns column 1, which is identical on every screen. `Workspace` owns columns 2 and 3,
 * and it is used BY the screen rather than around it, because column 2's content belongs to
 * whoever owns the data behind it: the roster's segments carry counts the roster has already
 * computed, and a shell that rendered them itself would have to load the roster a second time
 * to label a nav item. §7's finding, one layer out — the duplication invisible in source is the
 * one that is obvious in a screenshot.
 *
 * ── WHAT IS REFUSED HERE, EACH WITH ITS REASON ────────────────────────────────────────────
 *
 * • **A rail that opens for the pointer alone.** The rail was refused in §9.4, built on his 2026-08-29 word, retired by
 *   his W81 yes (2026-09-25) and BROUGHT BACK by his 2026-10-02 word (W140: *"make the left menu auto hide and we can have
 *   a lock/unlock button"*). What stays refused is the defect: `railIsOpen` ORs the pointer with FOCUS, so a keyboard
 *   opens it exactly as a mouse does; the open sidebar is an OVERLAY (the page never reflows); Escape closes it.
 * • **A flyout section header.** §29.8 measured theirs as a `div` with `cursor: pointer`, no
 *   `href`, no `role`, no `tabindex` — it advertises a click it does not have and no keyboard
 *   can reach it. It exists to say which panel you are in while the rail is collapsed, which is
 *   a problem an always-labelled rail does not have.
 * • **"Show more" that discloses nothing** (§29.3).
 * • **A page tree as the universal shape for column 2** (§9.1).
 *
 * ── AND THE ONE CLAIM THAT IS FREE AND FALSIFIABLE ────────────────────────────────────────
 *
 * §12 executed `document.querySelectorAll('main a[href],main button,main [tabindex]')` on their
 * training calendar and got ZERO, and §29.11 found the same `div` + `cursor: pointer` + no role
 * primitive on their add-work control — so it is how their front end is built rather than one
 * missed screen. Every item below is a real `<a href>` inside a real list inside a real `<nav>`,
 * so tabbing, opening in a new tab and copying a link all work without one line spent on them.
 */

export function Shell({
  route,
  session,
  children,
}: {
  route: Route;
  /** The signed-in coach: the card at the foot, and the two counts' owner. */
  session: Session;
  children: ReactNode;
}) {
  const counts = useShellCounts(session.user.id, route);
  const narrow = useNarrow();
  /* 🔴 THE SWITCH IS RENDERED EXACTLY ONCE, HERE (Fable's ruling on W81, 2026-09-26). One element, placed in ONE of two
     slots: the sidebar, directly above the coach's card — or, at phone width, where the bottom bar has no room, on the
     ACCOUNT page the bar's fourth icon leads to (route `page`), under that page's own content. Anywhere else at phone
     width it is not drawn: the stored choice was already applied before the first paint (`applyStoredTheme`, main.tsx),
     so a coach who never opens the account page still gets the theme they chose. */
  const themeSwitch = <ThemeControl />;
  const phoneAppearance = narrow && route.name === "page";

  /* 🔴 W140 (S1–S3, S6) — THE RAIL AND ITS LOCK. Locked = W81's sidebar, in the layout. Unlocked (the default) = the rail;
     the full sidebar is drawn OVER the page while the pointer is on it or focus is in it (`railIsOpen`). Leaving closes it
     after a short delay, so a diagonal path to the page does not flicker it. Escape closes it and leaves focus on a rail
     item. The lock is remembered per coach (`viewPrefs`). The stacked and phone layouts ignore all of it (S5, CSS). */
  const [locked, setLocked] = useState<boolean>(() => lockFromStored(readPref("sidebarLocked")));
  const [pointerInside, setPointerInside] = useState(false);
  const [focusInside, setFocusInside] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const open = railIsOpen(locked, pointerInside && !dismissed, focusInside && !dismissed);
  useEffect(() => () => { if (closeTimer.current !== null) window.clearTimeout(closeTimer.current); }, []);
  function toggleLock() {
    const next = !locked;
    setLocked(next);
    writePref("sidebarLocked", next ? "1" : "0");
  }
  function pointerEnter() {
    if (closeTimer.current !== null) { window.clearTimeout(closeTimer.current); closeTimer.current = null; }
    setDismissed(false);
    setPointerInside(true);
  }
  function pointerLeave() {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => { closeTimer.current = null; setPointerInside(false); }, RAIL_CLOSE_DELAY_MS);
  }
  /* KEYBOARD focus opens it (S6) — a mouse click on the lock or the theme switch also focuses, and that must not hold the
     sidebar open after the pointer has left, so only a `:focus-visible` arrival counts. */
  function focusIn(e: FocusEvent<HTMLElement>) {
    let visible = true;
    try { visible = e.target.matches(":focus-visible"); } catch { /* an engine without the selector: treat as keyboard */ }
    if (visible) setFocusInside(true);
  }
  function focusOut(e: FocusEvent<HTMLElement>) {
    if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
    setFocusInside(false);
    setDismissed(false);
  }
  function keyDown(e: KeyboardEvent<HTMLElement>) {
    if (e.key !== "Escape" || locked || !open) return;
    setDismissed(true);
    /* Focus stays IN the rail: on the item that had it when that item shows in the rail, else on the current section. */
    const active = document.activeElement;
    const keep = active instanceof HTMLElement && active.closest(".shell-section, .shell-brand, .shell-card") !== null;
    if (!keep) navRef.current?.querySelector<HTMLElement>(".shell-section[aria-current], .shell-section")?.focus();
  }
  const shellClass = `shell${locked ? "" : " is-rail"}${!locked && open ? " is-open" : ""}`;

  return (
    <div className={shellClass}>
      {/* Labelled, because a page may hold more than one navigation and "Sections" is what a screen reader should read
          out before the list. Every item keeps its word in the DOM in both states — the rail CLIPS the label, never
          removes it, so the link's name survives (W140). */}
      <nav className="shell-sections" aria-label="Sections" ref={navRef}
           onPointerEnter={pointerEnter} onPointerLeave={pointerLeave}
           onFocus={focusIn} onBlur={focusOut} onKeyDown={keyDown}>
        <div className="shell-head">
          {/* The brand is HIS (2026-09-26: *"we keep my logo, the one on the ios"*) — the phone's lockup, untouched, as a
              link home to the landing. */}
          <a className="shell-brand" href={href({ name: "dashboard" })}>
            <BrandLockup />
          </a>
          {/* S2 — the lock: Lucide's lock (stays) / lock-open (hides); its name is what pressing it does. */}
          <button type="button" className="shell-lock" aria-pressed={locked} aria-label={lockLabel(locked)}
                  title={lockLabel(locked)} onClick={toggleLock}>
            <Icon name={locked ? "lock" : "lock-open"} size={16} />
          </button>
        </div>

        <ul className="shell-section-list">
          {SECTIONS.map((s) => {
            const marker = currentMarker(route, s.id);
            const badge = sectionBadge(s.id, counts);
            return (
              <li key={s.id}>
                <a
                  className="shell-section ht-body-medium"
                  href={sectionHref(s)}
                  // "page" when this IS the page, "true" when the section merely contains it (`nav.ts`, executed).
                  aria-current={marker}
                  // The rail's tooltip: in the rail the word is clipped out of sight (still the link's name).
                  title={open ? undefined : s.label}
                >
                  <Icon name={s.icon} className="shell-glyph" />
                  <span className="shell-section-label">{s.label}</span>
                  {/* The count is part of the link's NAME ("Inbox, 4 unread") — the visible pill is decoration. */}
                  {badge !== null && (
                    <>
                      <span className="shell-badge" aria-hidden="true">{badge}</span>
                      <span className="ht-visually-hidden">, {badge} {s.id === "inbox" ? "unread" : "need you"}</span>
                    </>
                  )}
                </a>
              </li>
            );
          })}
        </ul>

        {/* Below the sections and apart from them: the switch, then the coach — these act on the ACCOUNT, not on where
            you are. */}
        <div className="shell-account">
          {!narrow && <div className="shell-theme">{themeSwitch}</div>}
          <CoachCard session={session} current={accountMarker(route)} />
        </div>
      </nav>

      {/* THE TOP BAR — ⌘K, the existing palette, one click away (W81). Hidden at phone width, where there is no ⌘. */}
      <div className="shell-topbar">
        <SearchControl />
      </div>

      {children}

      {phoneAppearance && (
        <section className="shell-phone-appearance" aria-labelledby="shell-phone-appearance-h">
          <h2 id="shell-phone-appearance-h" className="ht-overline">Appearance</h2>
          {themeSwitch}
        </section>
      )}
    </div>
  );
}

/** How long the open sidebar waits after the pointer leaves — long enough for a diagonal path from a rail icon to the
 *  page not to flicker it, short enough not to cover the page after a deliberate exit (chosen by trying; W140). */
export const RAIL_CLOSE_DELAY_MS = 220;

/** Phone width — the one breakpoint where the sidebar is the bottom bar (`width < 48rem`, direction.css's own band). */
function useNarrow(): boolean {
  const query = "(width < 48rem)";
  const [narrow, setNarrow] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return narrow;
}

/**
 * Columns 2 and 3 — the list you are choosing from, and the detail of that selection.
 *
 * `list` is optional: a section with nothing to choose from renders the detail across both
 * columns rather than an empty rail with a heading over it. `listLabel` names the region, so
 * the second navigation on the page is distinguishable from the first.
 *
 * ⚠️ `<main id="main">` STAYS INSIDE THE DETAIL, WHICH THE SCREENS ALREADY RENDER. That is what
 * makes the skip link land past BOTH columns of chrome rather than at the top of the list — and
 * it is why this component takes the detail as `children` instead of wrapping it in a `<main>`
 * of its own, which would have given the page two.
 */
export function Workspace({
  list,
  listLabel,
  children,
}: {
  list?: ReactNode;
  listLabel?: string;
  children: ReactNode;
}) {
  return (
    <div className={list === undefined ? "workspace workspace-nolist" : "workspace"}>
      {list !== undefined && (
        <div className="workspace-list" aria-label={listLabel} role={listLabel === undefined ? undefined : "region"}>
          {list}
        </div>
      )}
      {children}
    </div>
  );
}
